

import React, { useEffect } from 'react';
import { useLanguage } from './LanguageContext';

interface SEOProps {
  title?: string;
  description?: string;
  schema?: Record<string, any>;
  path?: string;
}

const SEO: React.FC<SEOProps> = ({ title, description, schema, path = '' }) => {
  const { language } = useLanguage();

  useEffect(() => {
    const siteName = language === 'he' ? 'יהודה פייט' : 'Yehuda Fait';
    const defaultDescription = language === 'he'
      ? 'הטשולנט האגדי של בני ברק. חוויה קולינרית יהודית אותנטית ברחוב הירקון 39. פתוח בימי חמישי.'
      : 'Legendary Cholent in Bnei Brak. Authentic Jewish culinary experience at 39 HaYarkon St. Open on Thursdays.';

    const fullTitle = title ? `${title} | ${siteName}` : `${siteName} | ${language === 'he' ? 'הטעם של השבת' : 'Taste of Shabbat'}`;
    const metaDescription = description || defaultDescription;
    const canonicalUrl = `https://yehuda-fait.com${path}`; // Replace with actual domain when live

    // 1. Update Title
    document.title = fullTitle;

    // 2. Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);

    // 3. Update Canonical Link
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // 4. Update Open Graph Tags (Basic)
    const updateMeta = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', property);
            document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
    };

    updateMeta('og:title', fullTitle);
    updateMeta('og:description', metaDescription);
    updateMeta('og:url', canonicalUrl);

    // 5. Update Schema (JSON-LD)
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": "Yehuda Fait",
      "alternateName": "יהודה פייט",
      "image": "https://yehuda-fait.com/images/hero-cholent.jpg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "HaYarkon 39",
        "addressLocality": "Bnei Brak",
        "addressCountry": "IL"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 32.096,
        "longitude": 34.825
      },
      "telephone": "054-847-8033",
      "priceRange": "$$",
      "servesCuisine": ["Jewish", "Kosher"],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Thursday",
          "opens": "13:00",
          "closes": "23:59"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Friday",
          "opens": "00:00",
          "closes": "03:00"
        }
      ]
    };

    const jsonLdData = schema ? { ...baseSchema, ...schema } : baseSchema;
    
    // Manage script tag
    const scriptId = 'seo-schema-json-ld';
    let scriptTag = document.getElementById(scriptId);
    
    if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
    }
    
    scriptTag.textContent = JSON.stringify(jsonLdData);

  }, [title, description, schema, path, language]);

  return null;
};

export default SEO;